import React, { useEffect, useState } from "react";
import PageHeaderAlt from "components/layout-components/PageHeaderAlt";
import { AutoComplete, Button, Form, InputNumber, message, Modal, Select, Tabs } from "antd";
import Flex from "components/shared-components/Flex";
import GeneralField from "./GeneralField";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { find, findIndex, forEach } from "lodash";
import { compressImage, getBase64 } from "functions/images";
import { useFirebase, useFirestore, useFirestoreConnect } from "react-redux-firebase";
import * as dayjs from "dayjs";
import {
  PRODUCT_COLORS,
  PRODUCT_DIMENSION_TYPE_HEIGHT_DIAMETER,
  PRODUCT_DIMENSION_TYPE_LENGTH_WIDTH,
  PRODUCT_DIMENSION_TYPE_LENGTH_WIDTH_HEIGHT,
  PRODUCT_IMAGE_COMPRESSION_QUALITY,
  PRODUCT_IMAGE_MAX_WIDTH,
} from "constants/ProductConstant";
import {
  formatNameToURL,
  formatPrice,
  formatPriceUsd,
  getFormattedProductColor,
} from "../../../../functions/formatters";
import VariantField from "./VariantField";
import GroupField from "./GroupField";
import { FIRESTORE_GROUPS_TABLE, FIRESTORE_PRODUCTS_TABLE } from "../../../../constants/FirestoreConstant";
import { deleteFilesFromStorage, saveFilesToStorage } from "../../../../functions/firebase/storage";
import { setRelatedProductVariants } from "../../../../functions/firestore/product";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { editProduct } from "../../../../urls";
import { getExactProduct, syncProductToExact } from "../../../../functions/api/exact";
import { convertEurToUsd, convertEurToIdr } from "../../../../functions/calculations";

const { TabPane } = Tabs;

const ADD = "ADD";
const EDIT = "EDIT";
const imagesPath = "products";

const ProductForm = (props) => {
  const { mode = ADD, param } = props;
  const firestore = useFirestore();
  const firebase = useFirebase();
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [imageList, setImageList] = useState([]);
  const [originalImageList, setOriginalImageList] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [imageOrderChanged, setImageOrderChanged] = useState(false);
  const [newImagesUploaded, setNewImagesUploaded] = useState(false);
  const [showReorderImageNotification, setShowReorderImageNotification] = useState(false);
  const [productDimensionType, setProductDimensionType] = useState(PRODUCT_DIMENSION_TYPE_HEIGHT_DIAMETER);
  const email = useSelector((state) => state.firebaseReducer.auth.email);
  const products = useSelector((state) => state.firestoreReducer.ordered.products);
  const categories = useSelector((state) => state.firestoreReducer.ordered.categories);
  // Deze pagina leest de collectie, dus abonneert de pagina zelf. Stond tot nu
  // toe in layouts/app-layout (op elke CMS-pagina) -- zie docs/cms-performance.md.
  useFirestoreConnect([{ collection: FIRESTORE_GROUPS_TABLE }]);
  const groups = useSelector((state) => state.firestoreReducer.ordered.groups) || [];
  const productId = !!props.param ? props.param.id : null;
  const clickedProduct = find(products, ["id", productId]);
  const [prevProduct, setPrevProduct] = useState(null);
  const [nextProduct, setNextProduct] = useState(null);
  const [globalPrevProduct, setGlobalPrevProduct] = useState(null);
  const [globalNextProduct, setGlobalNextProduct] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [exactProduct, setExactProduct] = useState(null);
  const [exactProductLoading, setExactProductLoading] = useState(false);
  const [productCode, setProductCode] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [priceConversionModal, setPriceConversionModal] = useState({
    open: false,
    eurPrice: 0,
    usdPrice: 0,
    idrPrice: 0,
    selectedCurrency: "USD",
    editedPrice: 0,
  });

  const { t } = useTranslation();

  useEffect(() => {
    if (mode === EDIT) {
      getStorageFiles().then((result) => {
        setImageList(result);
        setOriginalImageList(result);
      });
      form.setFieldsValue({
        url: !!clickedProduct.url ? clickedProduct.url : formatNameToURL(clickedProduct.name),
        name: clickedProduct.name,
        code: clickedProduct.code,
        barcode: clickedProduct.barcode || null,
        shortDescription: !!clickedProduct.shortDescription ? clickedProduct.shortDescription : null,
        description: clickedProduct.description,
        category: clickedProduct.category,
        price: clickedProduct.price,
        purchasePrice: !!clickedProduct.purchasePrice ? clickedProduct.purchasePrice : null,
        innerPackagingQuantity: !!clickedProduct.innerPackagingQuantity ? clickedProduct.innerPackagingQuantity : null,
        masterPackagingQuantity: !!clickedProduct.masterPackagingQuantity
          ? clickedProduct.masterPackagingQuantity
          : null,
        taxRate: clickedProduct.taxRate,
        discount: clickedProduct.discount,
        stock: clickedProduct.stock,
        min_amount: !!clickedProduct.min_amount ? clickedProduct.min_amount : 1,
        productItems: clickedProduct.productItems,
        additionalInfo: clickedProduct.additionalInfo,
        productVariants: clickedProduct.productVariants,
        variantName: clickedProduct.variantName,
        hideVariant: clickedProduct.hideVariant || false,
        discontinued: clickedProduct.discontinued || false,
        orderUnit: clickedProduct.orderUnit || 1,
        preorder_available_date: !!clickedProduct.preOrder
          ? dayjs(clickedProduct.preOrder.availableAt, "DD-MM-YYYY")
          : false,
        preorder_active: !!clickedProduct.preOrder ? clickedProduct.preOrder.active : false,
        groupAlternatives: clickedProduct.groupAlternatives,
        colorNew: clickedProduct.colorNew || clickedProduct.color,
        height: clickedProduct.height,
        diameter: clickedProduct.diameter,
        length: clickedProduct.length,
        width: clickedProduct.width,
        amountInOuterBox: clickedProduct.amountInOuterBox || 1,
        supplierMetadata: clickedProduct.supplierMetadata || [],
        material: clickedProduct.material,
      });
      if (!!clickedProduct.length && !!clickedProduct.width && !!clickedProduct.height)
        setProductDimensionType(PRODUCT_DIMENSION_TYPE_LENGTH_WIDTH_HEIGHT);
      else if (!!clickedProduct.height || !!clickedProduct.diameter)
        setProductDimensionType(PRODUCT_DIMENSION_TYPE_HEIGHT_DIAMETER);
      else setProductDimensionType(PRODUCT_DIMENSION_TYPE_LENGTH_WIDTH);

      // Always compute global (database) prev/next
      const productIndex = findIndex(products, ["id", productId]);
      if (productIndex !== -1 && products?.length > 0) {
        setGlobalPrevProduct(products[(productIndex + products.length - 1) % products.length]?.id);
        setGlobalNextProduct(products[(productIndex + 1) % products.length]?.id);
      }

      // Determine variant-scoped prev/next if variants exist, else fall back to global
      if (
        clickedProduct &&
        Array.isArray(clickedProduct.productVariants) &&
        clickedProduct.productVariants.length > 0
      ) {
        const variantIds = [productId, ...clickedProduct.productVariants.map((v) => v.productId)].filter(Boolean);
        const idx = variantIds.findIndex((id) => id === productId);
        const prevIdx = (idx + variantIds.length - 1) % variantIds.length;
        const nextIdx = (idx + 1) % variantIds.length;
        setPrevProduct(variantIds[prevIdx]);
        setNextProduct(variantIds[nextIdx]);
      } else {
        setPrevProduct(products[(productIndex + products.length - 1) % products.length]?.id);
        setNextProduct(products[(productIndex + 1) % products.length]?.id);
      }
    }
  }, [form, mode, param, props, clickedProduct]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  /**
   * Retrieve files from firebase storage with their links
   * @returns Array of images
   */
  function getStorageFiles() {
    let promises = [];
    if (!!clickedProduct.image) {
      clickedProduct.image.forEach((imageUrl, index) => {
        let imageRef = firebase.storage().refFromURL(imageUrl);
        promises.push(
          imageRef
            .getMetadata()
            .then((info) => {
              return {
                uid: info.name,
                url: imageUrl,
                name: info.name,
                path: info.fullPath,
              };
            })
            .catch(() => {
              // Image does not exist in storage anymore
              return {};
            }),
        );
      });
    }
    return Promise.all(promises);
  }

  const handleUploadChange = ({ fileList }) => {
    let uploadedFile = fileList[fileList.length - 1];
    if (!!uploadedFile && uploadedFile.status === "done") {
      setNewImagesUploaded(true);
      // Compress image before setting imageList state
      compressImage(uploadedFile.originFileObj, PRODUCT_IMAGE_COMPRESSION_QUALITY, PRODUCT_IMAGE_MAX_WIDTH).then(
        (compressedImage) => {
          let compressedImageClone = new Blob([compressedImage], {
            type: compressedImage.type,
          });
          compressedImageClone.name = compressedImage.lastModified + "-" + compressedImage.name;
          // Clone object, it may have some read-only properties
          fileList[fileList.length - 1] = {
            ...fileList[fileList.length - 1],
            originFileObj: compressedImageClone,
            name: compressedImage.lastModified + "-" + compressedImage.name,
            size: compressedImage.size,
          };
          setImageList(fileList);
        },
      );
    } else setImageList(fileList);
  };

  const handleUploadOrderChange = (fileList) => {
    setImageList(fileList);
    setImageOrderChanged(true);
    if (newImagesUploaded) setShowReorderImageNotification(true);
  };

  const handlePreviewCancel = () => {
    setPreviewVisible(false);
  };

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }

    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(file.name || file.url.substring(file.url.lastIndexOf("/") + 1));
  };

  const setProductLoggingProperties = (product, mode) => {
    if (mode === ADD) return { ...product, createdAt: dayjs().unix(), createdBy: email };
    else if (mode === EDIT) return { ...product, updatedAt: dayjs().unix(), updatedBy: email };
  };

  const formatProductUrl = (productName) => {
    if (mode === ADD) {
      let formattedName = formatNameToURL(productName);
      form.setFieldsValue({ url: formattedName });
    }
  };

  const handleProductCodeChange = (code) => {
    setProductCode(code);
    setExactProduct(null);
    setExactProductLoading(false);

    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Only search if in ADD mode and code has at least 6 characters
    if (mode === ADD && code && code.trim().length >= 6) {
      // Set new timeout for 1 second
      const timeout = setTimeout(() => {
        setExactProductLoading(true);
        getExactProduct(code.trim())
          .then((product) => {
            setExactProduct(product);
            setExactProductLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching Exact product:", error);
            setExactProductLoading(false);
          });
      }, 1000);

      setSearchTimeout(timeout);
    }
  };

  const handleUseExactProduct = async () => {
    if (exactProduct) {
      // Determine productDimensionType based on which dimension values are filled
      let productDimensionType = null;
      const hasLength = exactProduct.Length != null && exactProduct.Length !== "";
      const hasWidth = exactProduct.Width != null && exactProduct.Width !== "";
      const hasHeight = exactProduct.Height != null && exactProduct.Height !== "";
      const hasDiameter = exactProduct.Diameter != null && exactProduct.Diameter !== "";

      if (hasLength && hasWidth && hasHeight) {
        setProductDimensionType(PRODUCT_DIMENSION_TYPE_LENGTH_WIDTH_HEIGHT);
      } else if (hasHeight && hasDiameter) {
        setProductDimensionType(PRODUCT_DIMENSION_TYPE_HEIGHT_DIAMETER);
      } else if (hasLength && hasWidth) {
        setProductDimensionType(PRODUCT_DIMENSION_TYPE_LENGTH_WIDTH);
      }

      // Match color from API to PRODUCT_COLORS (case-insensitive)
      let matchedColor = null;
      if (exactProduct.Color) {
        const apiColor = exactProduct.Color.toLowerCase().trim();
        const colorMatch = PRODUCT_COLORS.find(
          (c) => c.name.toLowerCase() === apiColor || c.id.toLowerCase() === apiColor,
        );
        matchedColor = colorMatch ? colorMatch.id : null;
      }

      // Convert purchase price from EUR to USD and IDR
      const purchasePriceEur = exactProduct.CostPriceStandard || 0;
      const [usdResult, idrResult] = await Promise.all([
        convertEurToUsd(purchasePriceEur),
        convertEurToIdr(purchasePriceEur),
      ]);
      const purchasePriceUsd = usdResult.amountUsd;
      const purchasePriceIdr = idrResult.amountIdr;

      if ((!usdResult.success || !idrResult.success) && purchasePriceEur > 0) {
        messageApi.warning(t("exchange_rate_fetch_failed"));
      }

      // Set form values (without purchase price initially)
      form.setFieldsValue({
        name: exactProduct.Description || "",
        code: exactProduct.Code || "",
        barcode: exactProduct.Barcode || "",
        description: exactProduct.Notes || "",
        color: matchedColor,
        price: exactProduct.StandardSalesPrice || 0,
        stock: exactProduct.Stock || 0,
        taxRate: 21,
        weight: exactProduct.NetWeight || null,
        productDimensionType: productDimensionType,
        length: hasLength ? parseFloat(exactProduct.Length) : null,
        width: hasWidth ? parseFloat(exactProduct.Width) : null,
        height: hasHeight ? parseFloat(exactProduct.Height) : null,
        diameter: hasDiameter ? parseFloat(exactProduct.Diameter) : null,
      });

      // Format URL based on product name
      if (exactProduct.Description) {
        formatProductUrl(exactProduct.Description);
      }

      // Show modal for purchase price conversion
      if (purchasePriceEur > 0) {
        setPriceConversionModal({
          open: true,
          eurPrice: purchasePriceEur,
          usdPrice: purchasePriceUsd,
          idrPrice: purchasePriceIdr,
          selectedCurrency: "USD",
          editedPrice: purchasePriceUsd,
        });
      } else {
        form.setFieldsValue({ purchasePrice: 0 });
        messageApi.success(t("exact_product_loaded"));
      }

      setExactProduct(null);
    }
  };

  const onFinish = () => {
    setSubmitLoading(true);
    const omitFormValues = ["preorder_available_date", "preorder_active"];
    const newProduct = form.getFieldsValue(true, (obj) => !omitFormValues.includes(obj.name[0]));

    // Set additional items to null instead of undefined if not set
    let purchasePrice = form.getFieldValue("purchasePrice") || null;
    let innerPackagingQuantity = form.getFieldValue("innerPackagingQuantity") || null;
    let masterPackagingQuantity = form.getFieldValue("masterPackagingQuantity") || null;
    let productItems = form.getFieldValue("productItems") || null;
    let productVariants = form.getFieldValue("productVariants") || null;
    let additionalInfo = form.getFieldValue("additionalInfo") || null;
    let shortDescription = form.getFieldValue("shortDescription") || null;
    let description = form.getFieldValue("description") || null;
    let preorderAvailableDate = form.getFieldValue("preorder_available_date");
    let preorderActive = form.getFieldValue("preorder_active");
    let groupAlternatives = form.getFieldValue("groupAlternatives") || null;
    let supplierMetadata = form.getFieldValue("supplierMetadata") || null;
    let preorderObject = !!preorderAvailableDate
      ? {
          availableAt: dayjs(preorderAvailableDate).format("DD-MM-YYYY"),
          active: preorderActive,
        }
      : null;
    // Some attributes are set to an empty array when deleting them, set to null instead
    if (productItems && productItems.length === 0) productItems = null;
    if (productVariants && productVariants.length === 0) productVariants = null;
    if (groupAlternatives && groupAlternatives.length === 0) groupAlternatives = null;
    if (supplierMetadata && supplierMetadata.length === 0) supplierMetadata = null;
    newProduct.productItems = productItems;
    newProduct.productVariants = productVariants;
    newProduct.additionalInfo = additionalInfo;
    newProduct.shortDescription = shortDescription;
    newProduct.description = description;
    newProduct.preOrder = preorderObject;
    newProduct.groupAlternatives = groupAlternatives;
    newProduct.purchasePrice = purchasePrice;
    newProduct.innerPackagingQuantity = innerPackagingQuantity;
    newProduct.masterPackagingQuantity = masterPackagingQuantity;
    newProduct.supplierMetadata = supplierMetadata;
    if (productDimensionType === PRODUCT_DIMENSION_TYPE_LENGTH_WIDTH_HEIGHT) {
      newProduct.diameter = null;
    } else if (productDimensionType === PRODUCT_DIMENSION_TYPE_HEIGHT_DIAMETER) {
      newProduct.length = null;
      newProduct.width = null;
    } else {
      newProduct.height = null;
      newProduct.diameter = null;
    }

    // Add logging to product
    let productWithLogging = setProductLoggingProperties(newProduct, mode);

    if (imageList.length >= 1) {
      form
        .validateFields()
        .then(() => {
          if (!!productVariants) setRelatedProductVariants(firestore, productId, productVariants);

          /** ADD PRODUCT */
          if (mode === ADD) {
            let imageUrlArray = [];
            let fileArray = [];
            // Push images to array, we will add those to product image array.
            imageList.forEach((item) => {
              fileArray.push(item.originFileObj);
            });
            // Update the product with the new form data
            firestore
              .collection(FIRESTORE_PRODUCTS_TABLE)
              .add(productWithLogging)
              .then((docRef) => {
                saveFilesToStorage(firebase, imagesPath, fileArray, imageUrlArray, docRef.id).catch((err) =>
                  messageApi.error(err.message),
                );
                form.resetFields();
                setImageList([]);
                messageApi.success(t("product_added"), 4);
              })
              .catch((err) => messageApi.error(err.message));
          }
          /** EDIT PRODUCT */
          if (mode === EDIT) {
            let newFiles = [];
            let imageUrlArray = [];

            // Push existing files to imageUrlArray, we do not need to re-upload those.
            // Push new files to newFiles array, we will add those to product image array.
            imageList.forEach((item) => {
              if (!!item.url) imageUrlArray.push(item.url);
              else newFiles.push(item.originFileObj);
            });

            // Go over the original image list and check which ones are removed. Put those in corresponding arrays
            let toDeleteFromStorage = [];
            let toDeleteFromFirestore = [];
            forEach(originalImageList, function (value) {
              if (value.status === "removed") {
                toDeleteFromStorage.push(value.path);
                toDeleteFromFirestore.push(value.url);
              }
            });

            let imageUrls = [];
            // Save new files to storage if added
            if (newFiles.length > 0) {
              saveFilesToStorage(firebase, imagesPath, newFiles, imageUrlArray, productId)
                .then(() => {
                  updateProduct(productWithLogging);
                })
                .catch((err) => messageApi.error(err.message));
            } else if (imageOrderChanged) {
              imageList.forEach((image) => {
                let foundImage = find(imageList, (item) => item.url === image.url);
                if (!!foundImage) {
                  imageUrls.push(image.url);
                }
              });
              productWithLogging.image = imageUrls;
              updateProduct(productWithLogging);
            } else updateProduct(productWithLogging);

            // Delete files from storage if removed
            if (toDeleteFromStorage.length > 0) {
              deleteFilesFromStorage(firebase, firestore, toDeleteFromStorage, productId, toDeleteFromFirestore);
            }

            function updateProduct(updatedProduct) {
              // Update the product with the new form data
              firestore
                .collection(FIRESTORE_PRODUCTS_TABLE)
                .doc(productId)
                .update(updatedProduct)
                .then(() => {
                  messageApi.success(t("product_saved"), 4);
                  setOriginalImageList(imageList);
                  setShowReorderImageNotification(false);
                  setNewImagesUploaded(false);

                  if (clickedProduct.exactGuid) {
                    // Toon loading message
                    const hideLoading = messageApi.loading(t("syncing_to_exact"), 0);

                    // Synchroniseer naar Exact (optioneel, alleen als bepaalde velden zijn aangepast)
                    syncProductToExact(updatedProduct, clickedProduct.exactGuid)
                      .then((result) => {
                        hideLoading();
                        const { syncedKeys } = result;

                        // Vertaal de keys naar leesbare namen
                        const keyTranslations = {
                          name: t("product_name"),
                          code: t("code"),
                          barcode: t("barcode"),
                          description: t("description"),
                          price: t("price"),
                          purchasePrice: t("purchase_price"),
                          stock: t("stock"),
                          taxRate: t("tax_rate"),
                        };

                        if (!syncedKeys || syncedKeys.length === 0)
                          messageApi.success(t("no_values_to_sync_to_exact", 5));
                        else {
                          const syncedKeyNames = syncedKeys.map((key) => keyTranslations[key] || key).join(", ");
                          messageApi.success(t("product_synced_to_exact", { keys: syncedKeyNames }), 5);
                        }
                      })
                      .catch((err) => {
                        hideLoading();
                        console.error("Exact sync error:", err);
                        // Toon optioneel een waarschuwing, maar blokkeer de save niet
                        messageApi.warning(t("product_saved_exact_sync_failed"));
                      });
                  }
                })
                .catch((err) => messageApi.error(err.message));
            }
          }
          setSubmitLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setSubmitLoading(false);
          messageApi.error(t("enter_required_data"));
        });
    } else {
      setSubmitLoading(false);
      if (imageList.length < 1) messageApi.error(t("notifications.minimum_product_images"));
    }
  };

  return (
    <>
      {contextHolder}
      <Form
        layout="vertical"
        form={form}
        name="advanced_search"
        className="ant-advanced-search-form"
        initialValues={{
          discount: 0,
          stock: 0,
          min_amount: 1,
          hideVariant: false,
          discontinued: false,
          orderUnit: 1,
          amountInOuterBox: 1,
          preorder_active: false,
        }}
      >
        <PageHeaderAlt className="border-bottom" overlap>
          <div className="container">
            <Flex className="py-2" mobileFlex={false} justifyContent="between" alignItems="center">
              <div className={"mb-4"}>
                <h2>{mode === "ADD" ? t("add_new_product") : t("edit_product")} </h2>
                {mode === EDIT && !!prevProduct && !!nextProduct && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {!!globalPrevProduct && (
                      <Link to={editProduct + globalPrevProduct}>
                        <LeftOutlined />
                      </Link>
                    )}
                    <PrevNextProduct>
                      <Link to={editProduct + prevProduct}>
                        <LeftOutlined />
                      </Link>
                      {clickedProduct &&
                      Array.isArray(clickedProduct.productVariants) &&
                      clickedProduct.productVariants.length > 0 &&
                      !clickedProduct.hideVariant ? (
                        <Select
                          size="small"
                          value={clickedProduct.id}
                          onChange={(id) => navigate(editProduct + id)}
                          style={{ minWidth: 220, margin: "0 4px" }}
                          optionFilterProp="children"
                          showSearch
                          filterOption={(input, option) =>
                            (option?.children || "").toLowerCase().includes(input.toLowerCase())
                          }
                        >
                          {[clickedProduct.id, ...clickedProduct.productVariants.map((v) => v.productId)]
                            .map((id) => find(products, ["id", id]))
                            .filter(Boolean)
                            .map((p) => (
                              <Select.Option key={p.id} value={p.id}>
                                {p.name}
                                {!!p.color ? " - " + getFormattedProductColor(p.color) : ""}
                              </Select.Option>
                            ))}
                        </Select>
                      ) : (
                        <p>{clickedProduct?.name}</p>
                      )}
                      <Link to={editProduct + nextProduct}>
                        <RightOutlined />
                      </Link>
                    </PrevNextProduct>
                    {!!globalNextProduct && (
                      <Link to={editProduct + globalNextProduct}>
                        <RightOutlined />
                      </Link>
                    )}
                    <AutoComplete
                      style={{ width: 280, marginLeft: 8 }}
                      options={(products || []).map((p) => ({
                        value: p.id,
                        label: `${p.name}${p.code ? ` (${p.code})` : ""}`,
                      }))}
                      value={searchValue}
                      onSearch={(val) => setSearchValue(val)}
                      onChange={(val) => setSearchValue(val)}
                      filterOption={(input, option) =>
                        (option?.label || "").toLowerCase().includes(input.toLowerCase())
                      }
                      onSelect={(id) => {
                        setSearchValue("");
                        navigate(editProduct + id);
                      }}
                      placeholder={t("search") + "..."}
                    />
                  </div>
                )}
              </div>
              <div className="mb-3">
                <Button type="primary" onClick={() => onFinish()} htmlType="submit" loading={submitLoading}>
                  {mode === "ADD" ? t("add") : t("save")}
                </Button>
              </div>
            </Flex>
          </div>
        </PageHeaderAlt>
        <div className="container">
          <Tabs defaultActiveKey="1" style={{ marginTop: 30 }}>
            <TabPane tab={t("general")} key="1">
              <GeneralField
                form={form}
                mode={mode}
                formatProductUrl={formatProductUrl}
                products={products}
                fileList={imageList}
                handleUploadChange={handleUploadChange}
                handleUploadOrderChange={handleUploadOrderChange}
                categories={categories}
                handlePreview={handlePreview}
                previewVisible={previewVisible}
                previewImage={previewImage}
                previewTitle={previewTitle}
                handlePreviewCancel={handlePreviewCancel}
                productId={productId}
                showReorderNotification={showReorderImageNotification}
                clickedProduct={clickedProduct || null}
                productDimensionType={productDimensionType}
                setProductDimensionType={setProductDimensionType}
                handleProductCodeChange={handleProductCodeChange}
                exactProduct={exactProduct}
                exactProductLoading={exactProductLoading}
                handleUseExactProduct={handleUseExactProduct}
              />
            </TabPane>
            <TabPane tab={t("product_variants")} key="2">
              <VariantField products={products} productId={productId} />
            </TabPane>
            <TabPane tab={t("product_groups_tab")} key="3">
              <GroupField groups={groups} alternatives={!!clickedProduct ? clickedProduct.groupAlternatives : null} />
            </TabPane>
          </Tabs>
        </div>
      </Form>

      {/* Price Conversion Modal */}
      <Modal
        title={t("convert_purchase_price_title")}
        open={priceConversionModal.open}
        onOk={() => {
          form.setFieldsValue({ purchasePrice: priceConversionModal.editedPrice });
          setPriceConversionModal({ ...priceConversionModal, open: false });
          messageApi.success(t("exact_product_loaded"));
        }}
        onCancel={() => {
          form.setFieldsValue({ purchasePrice: priceConversionModal.eurPrice });
          setPriceConversionModal({ ...priceConversionModal, open: false });
          messageApi.success(t("exact_product_loaded"));
        }}
        okText={t("use_converted_price")}
        cancelText={t("use_eur_price")}
      >
        <p style={{ marginBottom: 16 }}>
          {t("convert_purchase_price_message_multi", {
            eurPrice: `€ ${formatPrice(priceConversionModal.eurPrice)}`,
            usdPrice: formatPriceUsd(priceConversionModal.usdPrice),
            idrPrice: `IDR ${priceConversionModal.idrPrice.toLocaleString("id-ID")}`,
          })}
        </p>
        <div style={{ marginBottom: 16 }}>
          <span style={{ marginRight: 8 }}>{t("select_currency")}:</span>
          <Select
            value={priceConversionModal.selectedCurrency}
            onChange={(value) => {
              const newPrice = value === "USD" ? priceConversionModal.usdPrice : priceConversionModal.idrPrice;
              setPriceConversionModal({
                ...priceConversionModal,
                selectedCurrency: value,
                editedPrice: newPrice,
              });
            }}
            style={{ width: 200 }}
            options={[
              { value: "USD", label: "USD - US Dollar" },
              { value: "IDR", label: "IDR - Indonesian Rupiah" },
            ]}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span>
            {t("purchase_price")} ({priceConversionModal.selectedCurrency}):
          </span>
          <InputNumber
            value={priceConversionModal.editedPrice}
            onChange={(value) => setPriceConversionModal({ ...priceConversionModal, editedPrice: value || 0 })}
            min={0}
            step={priceConversionModal.selectedCurrency === "IDR" ? 1000 : 0.01}
            precision={priceConversionModal.selectedCurrency === "IDR" ? 0 : 2}
            prefix={priceConversionModal.selectedCurrency === "IDR" ? "IDR" : "$"}
            style={{ width: 200 }}
            formatter={
              priceConversionModal.selectedCurrency === "IDR"
                ? (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                : undefined
            }
            parser={priceConversionModal.selectedCurrency === "IDR" ? (value) => value.replace(/\./g, "") : undefined}
          />
        </div>
      </Modal>
    </>
  );
};

const PrevNextProduct = styled.div`
  display: flex;

  p {
    font-size: 15px;
    width: 170px;
    margin-left: 4px;
    margin-right: 4px;
    text-align: center;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    margin-bottom: 0;
  }

  a {
    font-size: 15px;
    line-height: 27px;
  }
`;

export default ProductForm;
